import React, {useRef} from 'react';
import cx from 'classnames';

import {Text, FontVariant, ThemeConsumer} from '../../..';
import {KEYBOARD_EVENT_KEY} from '../../../constants/keyboard';

import styles from './CondensedTabs.module.scss';

type CondensedTabID = number | string;

type CondensedTab = {
    id: CondensedTabID;
    name: string;
    textEndContent?: React.ReactElement;
    testId: string;
};

type CondensedTabs = CondensedTab[];

export const CondensedTabs: React.FC<
    React.ComponentProps<'div'> & {
        sizeModifier: 'small' | 'medium' | 'large';
        variant?: 'pill' | 'underline';
        tabs: CondensedTabs;
        selectedTabID: CondensedTabID;
        onTabChange: (newTabID: CondensedTabID) => void;
        allowActiveTabToBeClicked?: boolean;
    }
> = ({
    allowActiveTabToBeClicked = false,
    tabs,
    selectedTabID,
    onTabChange,
    className,
    sizeModifier = 'large',
    variant = 'pill',
}) => {
    const tabRefs = useRef(new Map<CondensedTabID, HTMLButtonElement>());

    const moveTo = (index: number) => {
        const tab = tabs.at(index % tabs.length);
        if (!tab || tab.id === selectedTabID) {
            return;
        }
        tabRefs.current.get(tab.id)?.focus();
        onTabChange(tab.id);
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        index: number,
    ) => {
        switch (event.key) {
            case KEYBOARD_EVENT_KEY.arrowRight:
                moveTo(index + 1);
                break;
            case KEYBOARD_EVENT_KEY.arrowLeft:
                moveTo(index - 1 < 0 ? tabs.length - 1 : index - 1);
                break;
            case KEYBOARD_EVENT_KEY.home:
                moveTo(0);
                break;
            case KEYBOARD_EVENT_KEY.end:
                moveTo(tabs.length - 1);
                break;
            default:
                return;
        }
        event.preventDefault();
    };

    const tabCount = tabs.length;
    // As per accessibility guidelines, if there is only one tab, it should not be focusable, focusing the tab is perceived by users as unnecessary.
    const shouldAllowFocus = tabCount > 1;

    return (
        <ThemeConsumer>
            {({colorScheme: defaultColorScheme}) => {
                return (
                    <div
                        role="tablist"
                        className={cx(
                            defaultColorScheme,
                            styles[defaultColorScheme],
                            styles[sizeModifier],
                            styles.tabs,
                            variant === 'underline' && styles.underline,
                            className,
                        )}
                    >
                        {tabs.map(
                            ({name, id, textEndContent, testId}, index) => {
                                const isSelected = id === selectedTabID;
                                return (
                                    <button
                                        type="button"
                                        data-testid={testId}
                                        onClick={() => {
                                            if (
                                                !isSelected ||
                                                allowActiveTabToBeClicked
                                            ) {
                                                onTabChange(id);
                                            }
                                        }}
                                        onKeyDown={event =>
                                            handleKeyDown(event, index)
                                        }
                                        ref={node => {
                                            node
                                                ? tabRefs.current.set(id, node)
                                                : tabRefs.current.delete(id);
                                        }}
                                        role="tab"
                                        aria-selected={isSelected}
                                        // Roving tabindex: the tablist is a single tab stop.
                                        tabIndex={
                                            isSelected && shouldAllowFocus
                                                ? 0
                                                : -1
                                        }
                                        key={id}
                                        className={cx(
                                            styles.tab,
                                            styles[defaultColorScheme],
                                            styles[sizeModifier],
                                            {
                                                [styles.active]: isSelected,
                                                [styles.canClickActive]:
                                                    allowActiveTabToBeClicked,
                                                [styles.underline]:
                                                    variant === 'underline',
                                            },
                                        )}
                                    >
                                        <Text
                                            fontVariant={
                                                sizeModifier === 'small'
                                                    ? FontVariant.SmallBold
                                                    : FontVariant.BodyBold
                                            }
                                            className={cx({
                                                'p-relative': !!textEndContent,
                                            })}
                                        >
                                            {name}
                                            {textEndContent}
                                        </Text>
                                    </button>
                                );
                            },
                        )}
                    </div>
                );
            }}
        </ThemeConsumer>
    );
};
